import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { Job, Snapshot, SnapshotDestination, SnapshotSchedule } from '@/api/types'
import { pushJob } from '@/features/job/queries'

/** The backups an application already has, newest first. */
export function snapshotsQuery(applicationId: number) {
  return queryOptions({
    queryKey: qk.snapshots(applicationId),
    queryFn: (): Promise<Snapshot[]> =>
      unwrap(
        getApi().GET('/api/snapshots/application/{application_id}/', {
          params: { path: { application_id: applicationId } },
        }),
      ),
  })
}

export function useSnapshots(applicationId: number) {
  return useQuery(snapshotsQuery(applicationId))
}

/**
 * Where a snapshot may be kept.
 *
 * The instance's answer rather than the application's: a bucket is configured
 * for the deployment, so the list is the same for everything in it. It is asked
 * for because it can be *one* item -- an instance with no bucket configured
 * offers only the in-database destination, and a control offering one choice is
 * a control to leave out.
 */
export function snapshotDestinationsQuery() {
  return queryOptions({
    queryKey: qk.snapshotDestinations(),
    staleTime: Infinity,
    queryFn: (): Promise<SnapshotDestination[]> =>
      unwrap(getApi().GET('/api/snapshots/destinations/')),
  })
}

export function useSnapshotDestinations() {
  return useQuery(snapshotDestinationsQuery())
}

/**
 * Taking one now.
 *
 * A job, because a snapshot copies a whole application. It arrives in the job
 * list the way a duplicated application does, and the list of snapshots is
 * refetched when it lands rather than guessed at -- the name the server gives a
 * snapshot and its size are both its own answers.
 */
export function createSnapshotMutation(queryClient: QueryClient, applicationId: number) {
  return mutationOptions({
    mutationFn: ({
      name,
      destination,
    }: {
      name: string
      destination?: string
    }): Promise<Job> =>
      unwrap(
        getApi().POST('/api/snapshots/application/{application_id}/', {
          params: { path: { application_id: applicationId } },
          body: { name, ...(destination !== undefined && { destination }) },
        }),
      ) as Promise<Job>,
    onSuccess: (job) => {
      pushJob(queryClient, job)
    },
  })
}

export function useCreateSnapshot(applicationId: number) {
  return useMutation(createSnapshotMutation(useQueryClient(), applicationId))
}

export function deleteSnapshotMutation(queryClient: QueryClient, applicationId: number) {
  return mutationOptions({
    mutationFn: (snapshotId: number) =>
      unwrap(
        getApi().DELETE('/api/snapshots/{snapshot_id}/', {
          params: { path: { snapshot_id: snapshotId } },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.snapshots(applicationId) })
    },
  })
}

export function useDeleteSnapshot(applicationId: number) {
  return useMutation(deleteSnapshotMutation(useQueryClient(), applicationId))
}

/**
 * Putting a whole application back to what a snapshot holds.
 *
 * Also a job, and the one thing here that changes data outside this dialog --
 * so everything is invalidated when it lands, for the same reason a revert
 * does it: the response says a restore happened and not what any cached view of
 * the application should now hold.
 */
export function restoreSnapshotMutation(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: (snapshotId: number): Promise<Job> =>
      unwrap(
        getApi().POST('/api/snapshots/{snapshot_id}/restore/', {
          params: { path: { snapshot_id: snapshotId } },
        }),
      ) as Promise<Job>,
    onSuccess: async (job) => {
      pushJob(queryClient, job)
      await queryClient.invalidateQueries({ queryKey: qk.all })
    },
  })
}

export function useRestoreSnapshot() {
  return useMutation(restoreSnapshotMutation(useQueryClient()))
}

/**
 * The schedule an application backs itself up on, or nothing.
 *
 * A 404 is the answer "there is no schedule" rather than a failure, so it is
 * turned into `null` here instead of reaching the error boundary: an
 * application with no schedule is the state every application starts in.
 */
export function snapshotScheduleQuery(applicationId: number) {
  return queryOptions({
    queryKey: qk.snapshotSchedule(applicationId),
    queryFn: async (): Promise<SnapshotSchedule | null> => {
      const answer = await getApi().GET(
        '/api/snapshots/application/{application_id}/schedule/',
        { params: { path: { application_id: applicationId } } },
      )
      if (answer.response.status === 404) return null
      // Re-wrapped so the one error path stays `unwrap`'s: everything other
      // than the 404 above should fail exactly as any other request does.
      return unwrap(Promise.resolve(answer))
    },
  })
}

export function useSnapshotSchedule(applicationId: number) {
  return useQuery(snapshotScheduleQuery(applicationId))
}

/**
 * Setting the schedule, which replaces it rather than amending it.
 *
 * The endpoint takes the whole thing -- its own serializer requires the
 * interval on every request -- so the form sends every value it holds and the
 * ones the interval does not use go along unread. See `schedule.ts` for why
 * they are all in UTC by the time they get here.
 */
export function setSnapshotScheduleMutation(
  queryClient: QueryClient,
  applicationId: number,
) {
  return mutationOptions({
    mutationFn: (schedule: Record<string, unknown>): Promise<SnapshotSchedule> =>
      unwrap(
        getApi().PATCH('/api/snapshots/application/{application_id}/schedule/', {
          params: { path: { application_id: applicationId } },
          body: schedule,
        }),
      ),
    onSuccess: (schedule) => {
      queryClient.setQueryData(qk.snapshotSchedule(applicationId), schedule)
    },
  })
}

export function useSetSnapshotSchedule(applicationId: number) {
  return useMutation(setSnapshotScheduleMutation(useQueryClient(), applicationId))
}

export function removeSnapshotScheduleMutation(
  queryClient: QueryClient,
  applicationId: number,
) {
  return mutationOptions({
    mutationFn: () =>
      unwrap(
        getApi().DELETE('/api/snapshots/application/{application_id}/schedule/', {
          params: { path: { application_id: applicationId } },
        }),
      ),
    onSuccess: () => {
      queryClient.setQueryData(qk.snapshotSchedule(applicationId), null)
    },
  })
}

export function useRemoveSnapshotSchedule(applicationId: number) {
  return useMutation(removeSnapshotScheduleMutation(useQueryClient(), applicationId))
}
